import {
  FAILED_CONCLUSIONS,
  isHeartbeatStale,
  isReviewable,
  isWaitingOnDesign,
  type CiRun,
  type Task,
  type TaskKind,
  type TaskOrigin,
  type TaskPr,
} from '@shared/domain.ts';
import { awaitingApproval, byRiskThenAge, LANDED, needsYou, type IsAdmin } from '@shared/queues.ts';
import type { DataState } from './store.ts';

export { needsYou, awaitingApproval, byRiskThenAge, type IsAdmin } from '@shared/queues.ts';

/**
 * Which cut of the board you are looking at.
 *
 * The menu picks the queue; the filter row narrows it. They deliberately do not
 * offer the same cuts — having both do the same thing was the most confusing
 * part of the prototype.
 */
export type Queue =
  | 'tasks'
  | 'needs-you'
  | 'approval'
  | 'failed'
  | 'merging'
  | 'merged'
  | 'deployed'
  | 'snoozed'
  | 'trash';

export type StatusFilter = 'all' | 'review' | 'waiting' | 'approval' | 'working';
export type SourceFilter = 'all' | 'human' | 'agent' | 'import' | 'background';
/** `none` is a task nobody classified. */
export type KindFilter = 'all' | 'none' | TaskKind;
export type OriginFilter = 'all' | TaskOrigin;

export interface Filters {
  status: StatusFilter;
  source: SourceFilter;
  kind: KindFilter;
  origin: OriginFilter;
  category: string; // category id, or 'all'
  repo: string; // repo id, or 'all'
  from: string; // user id, or 'all'
  /** Free text from the search box; see `parseSearch`. Empty means no search. */
  query: string;
}

export const defaultFilters: Filters = {
  status: 'all',
  source: 'all',
  kind: 'all',
  origin: 'all',
  category: 'all',
  repo: 'all',
  from: 'all',
  query: '',
};

/** How the list is ordered. `created_desc` — newest raised first — is the default. */
export type SortOrder = 'created_desc' | 'created_asc' | 'updated_desc';

export const defaultSort: SortOrder = 'created_desc';

export const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: 'created_desc', label: 'Newest first' },
  { value: 'created_asc', label: 'Oldest first' },
  { value: 'updated_desc', label: 'Recently updated' },
];

/**
 * On its way in: approved, or auto-merge with a pull request open for the
 * merge driver to land. An auto-merge task with no pull request — a routine
 * that found nothing to fix, or refused the work — has nothing to merge, and
 * falls back to the ordinary queue like any other task.
 */
function isMerging(task: Task): boolean {
  if (task.status === 'approved') return true;
  return task.auto_merge && task.pr_state === 'open' && !LANDED.has(task.status) && task.status !== 'error';
}

export function queueOf(task: Task, isAdmin: IsAdmin, now: number): Queue[] {
  // Opened for the record, not for anyone to see — see `inQueue`, which is
  // what a caller wanting the background queue anyway should reach for.
  if (task.source === 'background') return [];
  // Outrank the ladder entirely: a person put the task here on purpose, and
  // that is the more current fact whatever status or agent state it carries.
  if (task.deleted_at) return ['trash'];
  if (task.snoozed_at) return ['snoozed'];

  const queues: Queue[] = [];
  if (task.status === 'error') queues.push('failed');
  else if (task.status === 'deployed') queues.push('deployed');
  else if (task.status === 'merged') queues.push('merged');
  else if (isMerging(task)) {
    queues.push('merging', 'tasks');
  } else queues.push('tasks');

  if (needsYou(task, isAdmin, now)) queues.push('needs-you');
  if (awaitingApproval(task, now)) queues.push('approval');
  return queues;
}

export function inQueue(
  task: Task,
  queue: Queue,
  isAdmin: IsAdmin,
  now: number,
  options: { includeBackground?: boolean } = {},
): boolean {
  // A fire-and-forget task is opened for the record, not for anyone to keep
  // an eye on — it has no callback token, so nothing is ever going to move it
  // off "fresh" the way a report would. Left in the ordinary queues it would
  // just sit there looking abandoned. `includeBackground` is how a caller
  // that is deliberately looking for these — the source filter set to
  // "background" — asks to see it anyway.
  if (task.source === 'background' && !options.includeBackground) return false;

  switch (queue) {
    case 'tasks':
      // Deliberately the superset of everything still in flight — approved work
      // included, because it is still in flight until the routine merges it.
      // A closed-without-merging pull request took the task out of flight the
      // same way landing it would, just without anything to show for it. A
      // trashed or snoozed task left in the same way, on purpose.
      return (
        !LANDED.has(task.status) &&
        task.status !== 'error' &&
        !task.archived_at &&
        !task.deleted_at &&
        !task.snoozed_at
      );
    case 'needs-you':
      return needsYou(task, isAdmin, now);
    case 'approval':
      return awaitingApproval(task, now);
    case 'failed':
      return task.status === 'error' && !task.archived_at && !task.deleted_at && !task.snoozed_at;
    case 'merging':
      return (
        !task.archived_at &&
        !task.deleted_at &&
        !task.snoozed_at &&
        isMerging(task)
      );
    case 'merged':
      return task.status === 'merged' && !task.deleted_at && !task.snoozed_at;
    case 'deployed':
      return task.status === 'deployed' && !task.deleted_at && !task.snoozed_at;
    case 'snoozed':
      // Trashing outranks snoozing — a task cannot be both places at once.
      return Boolean(task.snoozed_at) && !task.deleted_at;
    case 'trash':
      return Boolean(task.deleted_at);
  }
}

function matchesStatus(task: Task, filter: StatusFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'review':
      return isReviewable(task) && task.status !== 'approved' && !LANDED.has(task.status);
    case 'waiting':
      return task.agent_state === 'waiting_on_you';
    case 'approval':
      return task.status === 'signed_off' || task.status === 'approved';
    case 'working':
      return task.agent_state === 'working';
  }
}

/* ── search ─────────────────────────────────────────────────────────────── */

/**
 * One word of a search. A number can stand for a task or a pull request, and
 * which one the person meant is only sometimes said:
 *
 *   `#58`, `58`    task 58 *or* pull request 58 — whichever exists
 *   `task:58`     task 58 only
 *   `pr:58`, `pr58`, `pr#58` pull request 58 only
 *   a PR URL       that pull request, by number
 *
 * A bare `58` also still matches the text "58", so searching for a version
 * or a port number is not swallowed by the number rule.
 */
export type SearchTerm =
  | { kind: 'text'; text: string }
  | { kind: 'number'; n: number; text: string }
  | { kind: 'task'; n: number }
  | { kind: 'pr'; n: number };

export function parseSearch(query: string): SearchTerm[] {
  const terms: SearchTerm[] = [];
  for (const raw of query.trim().toLowerCase().split(/\s+/)) {
    if (!raw) continue;
    let m: RegExpMatchArray | null;
    if ((m = raw.match(/\/pull\/(\d+)/))) {
      terms.push({ kind: 'pr', n: Number(m[1]) });
    } else if ((m = raw.match(/^(?:pr|pull)[:#]?#?(\d+)$/))) {
      terms.push({ kind: 'pr', n: Number(m[1]) });
    } else if ((m = raw.match(/^task[:#]?#?(\d+)$/))) {
      terms.push({ kind: 'task', n: Number(m[1]) });
    } else if ((m = raw.match(/^#(\d+)$/))) {
      terms.push({ kind: 'number', n: Number(m[1]), text: '' });
    } else if ((m = raw.match(/^(\d+)$/))) {
      terms.push({ kind: 'number', n: Number(m[1]), text: raw });
    } else {
      terms.push({ kind: 'text', text: raw });
    }
  }
  return terms;
}

/** A task's pull requests, grouped once per search rather than per task. */
function prsByTask(taskPrs: Record<string, TaskPr>): Map<string, TaskPr[]> {
  const out = new Map<string, TaskPr[]>();
  for (const pr of Object.values(taskPrs)) {
    const list = out.get(pr.task_id);
    if (list) list.push(pr);
    else out.set(pr.task_id, [pr]);
  }
  return out;
}

/** Every piece of prose a person might remember a task by, lowercased. */
function searchText(task: Task, prs: readonly TaskPr[]): string {
  return [
    task.title,
    task.prompt,
    task.quote,
    task.status_note,
    task.last_message,
    task.agent_activity,
    task.branch,
    task.pr_title,
    task.pr_url,
    task.ci_summary,
    task.last_error,
    ...prs.flatMap((pr) => [pr.title, pr.url, pr.base_branch]),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
}

/**
 * Does the task match every term? Terms AND together, so `#58 dark` narrows
 * rather than widens.
 */
export function matchesSearch(task: Task, terms: readonly SearchTerm[], prs: readonly TaskPr[] = []): boolean {
  if (terms.length === 0) return true;
  const hasPr = (n: number) => task.pr_number === n || prs.some((pr) => pr.number === n);
  let text: string | null = null;
  const haystack = () => (text ??= searchText(task, prs));
  return terms.every((term) => {
    switch (term.kind) {
      case 'task':
        return task.ref === term.n;
      case 'pr':
        return hasPr(term.n);
      case 'number':
        return task.ref === term.n || hasPr(term.n) || (term.text !== '' && haystack().includes(term.text));
      case 'text':
        return haystack().includes(term.text);
    }
  });
}

export function visibleTasks(
  data: DataState,
  queue: Queue,
  filters: Filters,
  isAdmin: IsAdmin,
  now: number,
  sort: SortOrder = defaultSort,
): Task[] {
  const includeBackground = filters.source === 'background';
  const terms = parseSearch(filters.query);
  const searching = terms.length > 0;
  // A search looks past the queue: the task you are hunting for by number is
  // as likely to have merged last week as to be in flight. Trash stays out —
  // unless Trash is where you are searching.
  const inScope = searching
    ? (task: Task) => (queue === 'trash' ? Boolean(task.deleted_at) : !task.deleted_at)
    : (task: Task) => inQueue(task, queue, isAdmin, now, { includeBackground });
  const prs = searching ? prsByTask(data.taskPrs) : null;
  // The approval queue has one order, and it is the point of the queue: the
  // least risky first, so the ones that need only a glance go by in a glance.
  const order = queue === 'approval' && !searching ? byRiskThenAge : SORTERS[sort];
  return Object.values(data.tasks)
    .filter(inScope)
    .filter((task) => !prs || matchesSearch(task, terms, prs.get(task.id)))
    .filter((task) => matchesStatus(task, filters.status))
    .filter((task) => filters.source === 'all' || task.source === filters.source)
    .filter((task) => filters.kind === 'all' || (task.kind ?? 'none') === filters.kind)
    .filter((task) => filters.origin === 'all' || task.origin === filters.origin)
    .filter((task) => filters.category === 'all' || task.category_id === filters.category)
    .filter((task) => filters.repo === 'all' || task.repo_id === filters.repo)
    .filter((task) => filters.from === 'all' || task.created_by === filters.from)
    .sort(order);
}

/** One check at a branch's head, as the approval row lists it. */
export interface HeadCheckRow {
  name: string;
  tone: 'passing' | 'failing' | 'running' | 'none';
  url: string | null;
}

const TONE_ORDER: Record<HeadCheckRow['tone'], number> = { failing: 0, running: 1, none: 2, passing: 3 };

/**
 * Every check on the task's branch at its newest head, one per name.
 *
 * Read from the `ci_runs` rows the webhook already delivered, so it costs
 * nothing and agrees with the rollup chip beside it. Only the newest head
 * counts, and only the newest run of each name at it — the same "a re-run
 * outvotes what it replaced" rule the rollup trigger applies.
 */
export function headChecks(task: Task, ciRuns: Record<string, CiRun>): HeadCheckRow[] {
  if (!task.branch) return [];
  const runs = Object.values(ciRuns).filter(
    (run) => run.branch === task.branch && (!task.repo_id || run.repo_id === task.repo_id),
  );
  if (runs.length === 0) return [];
  const newestHead = runs.reduce((a, b) => (b.updated_at > a.updated_at ? b : a)).head_sha;

  const byName = new Map<string, CiRun>();
  for (const run of runs) {
    if (run.head_sha !== newestHead) continue;
    const seen = byName.get(run.name);
    if (!seen || run.updated_at > seen.updated_at) byName.set(run.name, run);
  }

  return [...byName.values()]
    .map((run): HeadCheckRow => ({
      name: run.name,
      url: run.html_url,
      tone:
        run.status !== 'completed'
          ? 'running'
          : FAILED_CONCLUSIONS.has(run.conclusion ?? '')
            ? 'failing'
            : run.conclusion === 'success'
              ? 'passing'
              : 'none',
    }))
    .sort((a, b) => TONE_ORDER[a.tone] - TONE_ORDER[b.tone] || a.name.localeCompare(b.name));
}

/**
 * Most recently touched first, with one exception: anything actively being
 * worked on floats to the top. The whole point of the board is to see what is
 * happening now.
 */
function byRecency(a: Task, b: Task): number {
  const aLive = a.agent_state === 'working' || a.ci_state === 'running';
  const bLive = b.agent_state === 'working' || b.ci_state === 'running';
  if (aLive !== bLive) return aLive ? -1 : 1;
  return Date.parse(b.updated_at) - Date.parse(a.updated_at);
}

function byCreated(direction: 1 | -1): (a: Task, b: Task) => number {
  return (a, b) => direction * (Date.parse(b.created_at) - Date.parse(a.created_at));
}

const SORTERS: Record<SortOrder, (a: Task, b: Task) => number> = {
  created_desc: byCreated(1),
  created_asc: byCreated(-1),
  updated_desc: byRecency,
};

export function queueCounts(data: DataState, isAdmin: IsAdmin, now: number): Record<Queue, number> {
  const counts: Record<Queue, number> = {
    tasks: 0,
    'needs-you': 0,
    approval: 0,
    failed: 0,
    merging: 0,
    merged: 0,
    deployed: 0,
    snoozed: 0,
    trash: 0,
  };
  for (const task of Object.values(data.tasks)) {
    for (const queue of Object.keys(counts) as Queue[]) {
      if (inQueue(task, queue, isAdmin, now)) counts[queue] += 1;
    }
  }
  return counts;
}

/* ── the two states, as the UI sees them ────────────────────────────────── */

/** In a quiet window it booked for itself, and not yet past the deadline. */
export function isQuiet(task: Task, now: number): boolean {
  return Boolean(task.agent_quiet_until && Date.parse(task.agent_quiet_until) > now);
}

export interface AgentPulse {
  live: boolean;
  stale: boolean;
  word: string;
  detail: string | null;
  tone: 'live' | 'waiting' | 'blocked' | 'quiet' | 'done';
}

function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Why a queued task is waiting, and when it is next looked at — the sentence
 * under "queued" on the row.
 */
export function queueDetail(task: Task, now: number): string | null {
  if (!task.queued_at) return null;
  const parts = [task.queue_reason ?? 'waiting for a routine'];
  if (task.queue_until && Date.parse(task.queue_until) > now) parts.push(`first back ${hhmm(task.queue_until)}`);
  if (task.queue_failures > 0 && task.queue_retry_at && Date.parse(task.queue_retry_at) > now) {
    parts.push(`trying again ${hhmm(task.queue_retry_at)}`);
  }
  return parts.join(' · ');
}

export function agentPulse(task: Task, now: number): AgentPulse {
  const stale = isHeartbeatStale(task, now);

  // Nobody has it: no routine could take it yet, or the one it went to
  // refused. The queue is retrying it, so this is a wait, not a failure — and
  // whatever the row said before it was queued is no longer true.
  if (task.queued_at) {
    return {
      live: false,
      stale: false,
      word: task.queue_failures > 0 ? `retrying · ${task.queue_failures} refused` : 'queued',
      detail: queueDetail(task, now),
      tone: 'quiet',
    };
  }

  // The routine asked for a pass with `resume` and ended its run. "not started"
  // would be a lie about a task that has been worked, and "working" a lie about
  // a routine that has gone — what is true is that it is waiting on the pass.
  if (
    isWaitingOnDesign(task) &&
    task.agent_state !== 'waiting_on_you' &&
    task.agent_state !== 'blocked'
  ) {
    return {
      live: false,
      stale: false,
      word: 'waiting on design',
      detail: task.design_activity ?? task.design_message,
      tone: 'quiet',
    };
  }

  if (task.agent_state === 'working') {
    // Inside a booked quiet window the routine is live by arrangement, not by
    // evidence, and saying "working" flat would overstate what we know. "heads
    // down" is the honest word: it is busy somewhere we cannot see it, and it
    // has named the moment it owes us a report.
    const heads = isQuiet(task, now);
    return {
      live: !stale,
      stale,
      word: stale ? 'no word' : heads ? 'heads down' : 'working',
      detail: task.agent_activity,
      tone: stale ? 'quiet' : 'live',
    };
  }
  if (task.agent_state === 'waiting_on_you') {
    return { live: false, stale: false, word: 'waiting on you', detail: task.agent_activity, tone: 'waiting' };
  }
  // Handed over, but say which of the two situations it is. "Ready for review"
  // over a task with no preview reads as an accusation that you are ignoring
  // it; "ready — no preview yet" says the truth, which is that the routine has
  // done its part and the thing you would look at has not arrived.
  if (task.agent_state === 'ready_for_review') {
    return {
      live: false,
      stale: false,
      word: isReviewable(task) ? 'ready for review' : 'ready — no preview yet',
      detail: task.agent_activity,
      tone: isReviewable(task) ? 'waiting' : 'quiet',
    };
  }
  // Replied to, and not yet picked up. Carries what it had asked, because that
  // is the thing your answer was about. If nobody collects it the sweep turns it
  // blocked, which is how an answer that went nowhere comes back into view.
  if (task.agent_state === 'answered') {
    return { live: false, stale: false, word: 'answered', detail: task.agent_activity, tone: 'quiet' };
  }
  if (task.agent_state === 'blocked') {
    return { live: false, stale: false, word: 'blocked', detail: task.last_error, tone: 'blocked' };
  }
  if (task.agent_state === 'done') {
    return { live: false, stale: false, word: 'finished', detail: null, tone: 'done' };
  }
  // "not sent" would be a lie about an adopted pull request — nothing was ever
  // going to be sent. What is true is that the invitation went out and nobody
  // has answered it, which is also the only thing a reader can act on.
  if (task.source === 'import' && task.listener !== 'routine' && !task.agent_heartbeat_at) {
    return {
      live: false,
      stale: false,
      word: task.claimed_at ? 'claimed, nothing reported yet' : 'nobody has claimed it',
      detail: null,
      tone: 'quiet',
    };
  }
  return {
    live: false,
    stale: false,
    word: task.dispatched_at ? 'not started' : 'not sent',
    detail: null,
    tone: 'quiet',
  };
}

export interface CiPulse {
  live: boolean;
  word: string;
  tone: 'passing' | 'failing' | 'running' | 'none';
  detail: string | null;
  url: string | null;
}

export function ciPulse(task: Task): CiPulse {
  switch (task.ci_state) {
    case 'passing':
      return { live: false, word: 'checks passed', tone: 'passing', detail: task.ci_summary, url: task.ci_url };
    case 'failing':
      return { live: false, word: task.ci_summary ?? 'checks failed', tone: 'failing', detail: task.ci_summary, url: task.ci_url };
    case 'running':
      return { live: true, word: 'building', tone: 'running', detail: task.ci_summary, url: task.ci_url };
    case 'queued':
      return { live: true, word: 'queued', tone: 'running', detail: task.ci_summary, url: task.ci_url };
    default:
      return { live: false, word: 'no checks', tone: 'none', detail: null, url: null };
  }
}

/** The single most useful sentence about a task, for the row. */
export function headline(task: Task, pulse: AgentPulse): string | null {
  if (pulse.tone === 'live' && task.agent_activity) return task.agent_activity;
  if (task.status_note) return task.status_note;
  if (task.last_message) return task.last_message;
  if (task.last_error) return task.last_error;
  return null;
}

/** Which one action this task actually wants from you, if any. */
export type Primary = 'approve' | 'sign-off' | 'retry' | 'send-back' | null;

export function primaryAction(task: Task, isAdmin: boolean): Primary {
  // An adopted pull request is never resent, so do not offer it. Something else
  // is already building that branch; dispatching a routine at it would set a
  // second one working over the top of the first, from a prompt that is only
  // the pull request description. The server refuses this too — a button whose
  // only outcome is an error message should not be on the screen.
  //
  // Unless Rigg has already handed it to a routine itself: then the routine is
  // Rigg's to resend, and re-dispatch is the only way to reach it.
  const resendable = task.source !== 'import' || task.listener === 'routine';
  if (task.status === 'error') return resendable ? 'retry' : null;
  // Never reached a routine: the queue gave up on it, or a refusal it is
  // backing off from may be fixed by now. Sending it straight away is the one
  // thing a person can usefully do. A task waiting for capacity gets nothing —
  // pressing send would only queue it again.
  if (resendable) {
    if (task.queued_at && task.queue_failures > 0) return 'retry';
    if (!task.queued_at && task.status === 'fresh' && !task.dispatched_at && task.last_error) return 'retry';
  }
  if (LANDED.has(task.status) || task.status === 'approved') return null;
  // A closed-without-merging pull request took the task out of flight — there
  // is nothing left here for anyone to approve.
  if (task.archived_at) return null;
  // In the trash or parked — nothing here for anyone to act on until it comes
  // back.
  if (task.deleted_at || task.snoozed_at) return null;
  // Already approved, at the moment it was raised. Offering the button implies
  // the merge is waiting on it, which it is not — the routine has been told it
  // may land as soon as the checks are green. Except where the pull request
  // touches a protected path: then the merge IS waiting on the button.
  if (task.auto_merge && !(task.merge_held_by && !task.approved_at)) return null;
  // An admin can approve any active task, not only a reviewable one — there is
  // no need to wait for a preview link before saying "land this". Approving
  // before a pull request exists queues the "approved for merge" comment on
  // the task; it is delivered the moment one opens.
  if (isAdmin) return 'approve';
  if (task.status === 'signed_off') return null;
  if (isReviewable(task)) return 'sign-off';
  return null;
}

/* ── review & merge timing ──────────────────────────────────────────────── */

export interface DurationStat {
  /** Median span, in ms, over whatever tasks currently carry both timestamps. */
  medianMs: number | null;
  /** How many tasks the median was drawn from — the Overview shows this next to it. */
  count: number;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

/**
 * How long a request takes to have something to look at, and how long an
 * approved task takes to actually land.
 *
 * Both are medians over whichever tasks happen to carry both endpoints —
 * there is no time window, because the queues this draws from (a project's
 * whole task list) are usually too small to slice further without the count
 * dropping to a handful. `count` is reported alongside so a small sample
 * reads as one, rather than as false precision.
 */
export function reviewTimings(tasks: Task[]): { toReviewable: DurationStat; toMerge: DurationStat } {
  const toReviewable: number[] = [];
  const toMerge: number[] = [];
  for (const task of tasks) {
    if (task.first_reviewable_at) {
      toReviewable.push(Date.parse(task.first_reviewable_at) - Date.parse(task.created_at));
    }
    if (task.approved_at && task.merged_at) {
      const span = Date.parse(task.merged_at) - Date.parse(task.approved_at);
      // Auto-merge tasks get approved_at backfilled to merged_at when they land
      // with no approval of their own — a real zero, not a negative to drop.
      if (span >= 0) toMerge.push(span);
    }
  }
  return {
    toReviewable: { medianMs: median(toReviewable), count: toReviewable.length },
    toMerge: { medianMs: median(toMerge), count: toMerge.length },
  };
}

/* ── deploys ─────────────────────────────────────────────────────────────── */

/**
 * Deploys are workflow runs Rigg has already been told about; all that is
 * missing is which name means what. A project names one workflow as the
 * preview and one as production, and these turn the rows into the two answers
 * people actually want: can I look at it yet, and is it out?
 *
 * Nothing is inferred from a name — "Deploy" means production in one repository
 * and a preview in the next — so an unnamed workflow is simply not a deploy.
 */
export interface Deploy {
  state: 'running' | 'passing' | 'failing' | 'none';
  /** What to call it, in a sentence. */
  word: string;
  at: string | null;
  url: string | null;
}

const NO_DEPLOY: Deploy = { state: 'none', word: 'not deployed', at: null, url: null };

function newest(runs: CiRun[]): CiRun | null {
  let best: CiRun | null = null;
  for (const run of runs) {
    if (!best || run.updated_at > best.updated_at) best = run;
  }
  return best;
}

function describe(run: CiRun | null, verb: string, done = 'deployed'): Deploy {
  if (!run) return NO_DEPLOY;
  const at = run.completed_at ?? run.updated_at;
  if (run.status !== 'completed') {
    return { state: 'running', word: `${verb}…`, at: run.started_at ?? run.updated_at, url: run.html_url };
  }
  if (run.conclusion === 'success') {
    return { state: 'passing', word: done, at, url: run.html_url };
  }
  // `skipped` (and `neutral`, `stale`) is not a failure — it is a workflow
  // deciding, on purpose, that this run had nothing to do: an `if:` gate on a
  // label, a path filter, a condition on the event that triggered it. Nothing
  // was attempted, so nothing failed. A repo that gates its preview workflow
  // on a "preview" label produces exactly this conclusion on every PR without
  // the label — and counting it as "deploy failed" sent people looking for a
  // broken deploy that never ran. Same FAILED_CONCLUSIONS/IGNORED_CONCLUSIONS
  // split the CI rollup already uses, so a deploy and a check agree about
  // what counts as failing.
  if (!FAILED_CONCLUSIONS.has(run.conclusion ?? '')) return NO_DEPLOY;
  return { state: 'failing', word: `${verb} failed`, at, url: run.html_url };
}

/** The preview for this task's branch, if the project named a preview workflow. */
export function previewDeploy(
  task: Task,
  ciRuns: Record<string, CiRun>,
  workflow: string | null | undefined,
): Deploy {
  if (!workflow || !task.branch) return NO_DEPLOY;
  return describe(
    newest(
      Object.values(ciRuns).filter(
        (run) => run.name === workflow && run.branch === task.branch,
      ),
    ),
    'deploying preview',
    // Never a bare "deployed": on a row that also says Merged, that reads as
    // "it is live", and a preview is the one deploy that says nothing about it.
    'preview deployed',
  );
}

/** Production, which belongs to the project rather than to any one task. */
export function prodDeploy(
  ciRuns: Record<string, CiRun>,
  workflow: string | null | undefined,
): Deploy {
  if (!workflow) return NO_DEPLOY;
  return describe(
    newest(Object.values(ciRuns).filter((run) => run.name === workflow)),
    'deploying',
  );
}

/**
 * Whether a task's work is actually in production.
 *
 * Merged is not deployed — that is the gap this closes. The test is whether a
 * production deploy *succeeded after the merge*: a run that started before it
 * cannot contain it. Timestamps rather than commit ancestry, because Rigg does
 * not hold the graph and being one deploy conservative is the safe direction.
 */
export function inProduction(
  task: Task,
  ciRuns: Record<string, CiRun>,
  workflow: string | null | undefined,
): Deploy {
  if (!workflow || !task.merged_at) return NO_DEPLOY;

  const since = Object.values(ciRuns).filter(
    (run) =>
      run.name === workflow &&
      run.status === 'completed' &&
      (run.completed_at ?? run.updated_at) > task.merged_at!,
  );
  const shipped = newest(since.filter((run) => run.conclusion === 'success'));
  if (shipped) return describe(shipped, 'deploying');

  // Merged, and either nothing has gone out since or what did go out failed.
  const running = newest(
    Object.values(ciRuns).filter((run) => run.name === workflow && run.status !== 'completed'),
  );
  if (running) return describe(running, 'deploying');

  return { state: 'none', word: 'merged, not yet in production', at: null, url: null };
}
